import { CategoryType } from "../models/enums/categoryType";
import { PermissionType } from "../models/enums/permissionType";
import { RoleType } from "../models/enums/roleType";
import { storageService } from "./storageService";
import { jwtDecode, JwtPayload } from 'jwt-decode';

type CustomJwtPayload = JwtPayload & {
    'is-admin'?: string;
    role: string | string[];
    esiaId?: string;
};

class PermissionService {
    private decodeToken(): CustomJwtPayload | null {
        const tokenData = storageService.getTokenData();
        if (!tokenData) return null;

        try {
            return jwtDecode<CustomJwtPayload>(tokenData.accessToken);
        } catch (error) {
            console.error("Error decoding token: ", error);
            return null;
        }
    }

    private isAdmin(decodedToken: CustomJwtPayload): boolean {
        const isAdminFlag = decodedToken['is-admin']?.toLowerCase() === 'true';
        const hasAdminRole = Array.isArray(decodedToken.role)
            ? decodedToken.role.includes('admin')
            : decodedToken.role === 'admin';

        return isAdminFlag || hasAdminRole;
    }

    canView(category: CategoryType) {
        const decoded = this.decodeToken();
        if (!decoded) return false;

        return this.isAdmin(decoded) || Object.hasOwnProperty.call(decoded, category);
    }

    hasRole(role: RoleType) {
        const decoded = this.decodeToken();
        if (!decoded) return false;

        const userRoles = decoded.role;
        if (Array.isArray(userRoles)) {
            return userRoles.includes(role);
        }
        return userRoles === role;
    }

    getRoles(): RoleType[] {
        const decoded = this.decodeToken();
        if (!decoded) return [];

        const userRoles = decoded.role;
        if (Array.isArray(userRoles)) {
            return userRoles.filter((role): role is RoleType =>
                Object.values(RoleType).includes(role as RoleType)
            );
        }
        return userRoles && Object.values(RoleType).includes(userRoles as RoleType)
            ? [userRoles as RoleType]
            : [];
    }

    canDo(category: CategoryType, permission: PermissionType) {
        const decoded = this.decodeToken();
        if (!decoded) return false;

        const permissionKey = `${category}.${permission}`;
        return this.isAdmin(decoded) || Object.hasOwnProperty.call(decoded, permissionKey);
    }

    isEsiaLinked(): boolean {
        const decoded = this.decodeToken();
        return !!decoded?.esiaId;
    }
}

export const permissionService = new PermissionService();